import { CapacitorHttp } from "@capacitor/core";
import { getRequestApiUrl, getApiRequestHeaders, getPreBootstrapConfig } from "../util/ApiHelper";
import { getApiSession, setApiSession } from "../util/ApiOptionsHelper";
import { Preferences } from '@capacitor/preferences';

//TODO: move to a common place later
declare global {
  var globalInitialized: boolean;
  var abpLoginInProgress: boolean;
  var abpRevalidateSession: boolean;
  var isLoginSuccessful: boolean;
  var fromReturn: boolean;
  var customLogOutUrl: string;
}

const MAX_RELOGIN_ATTEMPTS = 4;
const MAX_SESSION_TIMEOUT_SEC = 1800;

//initialize global values
if (!globalThis.globalInitialized) {
  globalThis.abpLoginInProgress = false;
  globalThis.abpRevalidateSession = false;
  globalThis.globalInitialized = true;
  globalThis.isLoginSuccessful = false;
  globalThis.fromReturn = false;
  globalThis.customLogOutUrl = '';
}

const canAttemptLogin = async () => {
  const now = Date.now();
  const startTime = (await Preferences.get({ key: 'loginReattemptStartTime' })).value;
  const count = parseInt((await Preferences.get({ key: 'loginReattemptCount' })).value || '0', 10);

  if (startTime) {
    const diffSec = Math.abs((now - parseInt(startTime, 10)) / 1000);
    if (diffSec > MAX_SESSION_TIMEOUT_SEC) {
      await Preferences.set({ key: 'loginReattemptStartTime', value: String(now) });
      await Preferences.set({ key: 'loginReattemptCount', value: '1' });
      return { allowed: true };
    }
    if (count >= MAX_RELOGIN_ATTEMPTS) {
      await Preferences.remove({ key: 'loginReattemptStartTime' });
      await Preferences.remove({ key: 'loginReattemptCount' });
      return { allowed: false, reason: 'Too many attempts.' };
    }
    await Preferences.set({ key: 'loginReattemptCount', value: String(count + 1) });
    return { allowed: true };
  } else {
    await Preferences.set({ key: 'loginReattemptStartTime', value: String(now) });
    await Preferences.set({ key: 'loginReattemptCount', value: '1' });
    return { allowed: true };
  }
}

export const initializeAbpSession = async (endActiveSession: boolean = false) => {
  const attempt = await canAttemptLogin();
  if (!attempt.allowed) {
    return { validLogin: false, message: attempt.reason };
  }
  if (!globalThis.abpLoginInProgress) {
    globalThis.abpLoginInProgress = true;
    const sessionValid = await abpLogin(endActiveSession);
    globalThis.abpLoginInProgress = false;
    return sessionValid;
  }
  return { validLogin: true, message: '' }
};

export const abpLogout = async () => {
  const response = await Promise.all([getApiRequestHeaders(), getRequestApiUrl("abp/logout/")]).then(async (values) => {
    const headers = values[0];
    const url = values[1];

    const options = {
      url: url,
      headers: headers,
    };
    return await CapacitorHttp.post(options);
  });
  return response;
};

export const getCustomLogOutUrl = async () => {
  const response = await Promise.all([getApiRequestHeaders(), getRequestApiUrl("LegalUnits/GetLogOutUrl")]).then(async (values) => {
    const headers = values[0];
    const url = values[1];

    const options = {
      url: url,
      headers: headers,
    };
    return await CapacitorHttp.get(options).then((response) => {
      //if authorization fails or some error occurs, return and don't set configuredLogOutUrl
      if(response.status !== 200)
      {
        return;
      }
      globalThis.customLogOutUrl = response.data;
      return;
    }).catch((error) => {
      return;
    });
  });
  return response;
};

const abpLogin = async (endActiveSession: boolean) => {
  const response = await Promise.all([getApiRequestHeaders(), getApiSession(), getRequestApiUrl("abp/login/"), getPreBootstrapConfig()]).then(async (values) => {
    const headers = values[0];
    const apiSession = values[1];
    const url = values[2];
    const preBootstrapConfig = values[3];

    const options = {
      url: url,
      headers: headers,
      data: {
        "extraLoginInfo":
          `domainHint=${preBootstrapConfig.ABP_DOMAIN_HINT}&database=${apiSession.DatabaseName}&plant=${apiSession.PlantCode}`,
          "endActiveSession": endActiveSession
      },
       webFetchExtra: {
        credentials: "include" as RequestCredentials
      }
    }
    const resp = CapacitorHttp.post(options).then(async (response) => {
      if (response.status === 200 && !!response.headers["x-tw-licsession-id"]) {
        apiSession.LicenseSessionId = response.headers["x-tw-licsession-id"];
        apiSession.LicenseIssuedAt = new Date().valueOf();
        apiSession.LicenseExpiration = 0; //do not use
        await setApiSession(apiSession);
        await Preferences.remove({ key: 'loginReattemptStartTime' });
        await Preferences.remove({ key: 'loginReattemptCount' });
        globalThis.isLoginSuccessful = true;
        return { validLogin: true, message: '' }
      } 
      else if (response.data.resultCode && response.data.resultCode === 3) {
        return { validLogin: false, message: response.data.errorMessage, resultCode: response.data.resultCode }
      }
      else {
        return { validLogin: false, message: response.data === "" ? "Invalid URL" : response.data.errorMessage }
      }
    }).catch(async (error) => {
      return { validLogin: false, message: "Invalid URL" }
    });
    return resp;
  });
  return response;
};
